import { supabase, waitForSupabaseAccessToken } from "@/lib/supabase";
import { ADMIN_PAGE_SIZE } from "@/lib/constants";

/**
 * Server-authoritative admin gate shared by the admin adapters. Throws unless
 * the current session passes the `is_admin` RPC. (RLS is the real boundary;
 * this fails fast before issuing a query.)
 *
 * Waits for the Privy→Supabase token exchange before calling the RPC:
 * requests issued before the token is ready run as `anon`, which has no
 * EXECUTE on is_admin() (revoked in 20260630000002) and would surface as
 * Postgres 42501 noise in Sentry instead of a clean local failure.
 */
/**
 * Auth-state failure (token exchange not ready, or the request lost its token
 * mid-flight and ran as `anon`) — distinct from a genuine non-admin denial so
 * Sentry grouping and callers can tell "re-login" apart from "not an admin".
 * Keeps the "Admin access required" prefix so substring matches still hold.
 */
const AUTH_NOT_READY_MESSAGE = "Admin access required: authentication not ready";

export async function assertAdmin(): Promise<void> {
  const token = await waitForSupabaseAccessToken();
  if (!token) {
    throw new Error(AUTH_NOT_READY_MESSAGE);
  }
  const { data, error } = await supabase.rpc("is_admin");
  if (error) {
    // 42501 = EXECUTE denied, meaning the RPC ran as `anon`: the token cache
    // was cleared between the wait above and the request (e.g. logout while a
    // guard call was in flight). An auth-state failure, not an admin verdict.
    if (error.code === "42501") {
      throw new Error(AUTH_NOT_READY_MESSAGE);
    }
    throw new Error("Admin access required");
  }
  if (!data) {
    throw new Error("Admin access required");
  }
}

export { ADMIN_PAGE_SIZE };
